<script setup lang="ts">
import { Construction, Lock, SearchX, TriangleAlert, WifiOff } from "@lucide/vue";
import { computed } from "vue";
import { isProblem } from "@/api/client";
import { Button } from "@/components/ui/button";
import { t } from "@/copy/strings";
import { cn } from "@/lib/utils";

/**
 * Explains a failed request in human terms: not built yet, no access, not
 * found, offline, or a server error (with retry).
 */
const props = defineProps<{ error: unknown; compact?: boolean; class?: string; what?: string }>();
defineEmits<{ retry: [] }>();

const view = computed(() => {
  const err = props.error;
  if (isProblem(err)) {
    if (err.notAvailable) return { icon: Construction, title: t.errors.notAvailableTitle, body: t.errors.notAvailableBody, retry: false, tone: "muted" };
    if (err.status === 501) return { icon: Construction, title: t.errors.notAvailableTitle, body: err.detail ?? t.errors.aiUnavailable, retry: false, tone: "muted" };
    if (err.status === 403) return { icon: Lock, title: t.errors.noAccessTitle, body: err.detail ?? t.errors.forbidden, retry: false, tone: "muted" };
    if (err.status === 404) return { icon: SearchX, title: props.what ? `${props.what} not found` : t.errors.notFound, body: err.detail ?? t.errors.pageNotFoundBody, retry: false, tone: "muted" };
    if (err.status === 0) return { icon: WifiOff, title: "Offline", body: t.errors.network, retry: true, tone: "warn" };
    return { icon: TriangleAlert, title: err.title || t.errors.generic, body: err.detail ?? "", retry: true, tone: "error" };
  }
  return { icon: TriangleAlert, title: t.errors.generic, body: (err as Error)?.message ?? "", retry: true, tone: "error" };
});
</script>

<template>
  <div
    :class="
      cn(
        'flex flex-col items-center justify-center gap-1.5 text-center',
        compact ? 'surface-inset px-4 py-6' : 'rounded-xl border border-dashed border-border-strong px-6 py-12',
        props.class,
      )
    "
    role="status"
  >
    <div
      :class="
        cn(
          'mb-1.5 grid size-10 place-items-center rounded-xl',
          view.tone === 'error' ? 'bg-destructive-soft text-destructive' : view.tone === 'warn' ? 'bg-warning-soft text-warning-foreground' : 'bg-muted text-muted-foreground',
        )
      "
    >
      <component :is="view.icon" class="size-5" aria-hidden="true" />
    </div>
    <p class="text-sm font-semibold text-balance">{{ view.title }}</p>
    <p v-if="view.body" class="max-w-md text-sm text-pretty text-muted-foreground [overflow-wrap:anywhere]">{{ view.body }}</p>
    <Button v-if="view.retry" variant="outline" size="sm" class="mt-2" @click="$emit('retry')">{{ t.common.retry }}</Button>
  </div>
</template>
